import React, { useState, useEffect } from 'react';
import { Moon, Sun, Menu, X, Github, Linkedin, Mail, Phone, Download, ExternalLink, Code, Palette, Briefcase, GraduationCap, Award, BarChart3, Laptop } from 'lucide-react';

export default function Portfolio() {
  const [darkMode, setDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [activeTab, setActiveTab] = useState('about');
  const [currentRole, setCurrentRole] = useState(0);
  const [portfolioTab, setPortfolioTab] = useState('software');

  // Resume path - correctly formatted for public folder
  const resumePath = `${process.env.PUBLIC_URL}/images/resume.pdf`;

  const roles = ['Full Stack Developer', 'ML Engineer', 'Data Analyst', 'Visual Artist'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'portfolio', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const skills = [
    { name: 'JavaScript', level: 85, color: 'from-yellow-400 to-yellow-600' },
    { name: 'React', level: 85, color: 'from-blue-400 to-blue-600' },
    { name: 'Git', level: 80, color: 'from-orange-400 to-red-500' },
    { name: 'Tailwind CSS', level: 70, color: 'from-cyan-400 to-blue-500' },
    { name: 'Node.js', level: 60, color: 'from-green-400 to-green-600' },
    { name: 'HTML', level: 95, color: 'from-red-400 to-red-600' },
    { name: 'R', level: 75, color: 'from-blue-500 to-indigo-600' },
    { name: 'Chakra UI', level: 65, color: 'from-teal-400 to-teal-600' },
  ];

  const services = [
    {
      icon: <Code className="w-8 h-8" />,
      title: 'Frontend Development',
      description: 'Building responsive, accessible, and performant user interfaces with modern technologies.',
      features: ['React applications', 'JavaScript integration', 'State management solutions', 'Component libraries'],
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: 'UI/UX Design',
      description: 'Creating intuitive user experiences with pixel-perfect interfaces that drive engagement.',
      features: ['User research & testing', 'Wireframing & prototyping', 'Interaction design', 'Design systems'],
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'DataBreach Analysis',
      description: 'Transforming raw data into actionable insights through advanced analytics and machine learning.',
      features: ['Python/ML data analysis', 'Statistical modeling', 'Business intelligence', 'Predictive analytics'],
      gradient: 'from-green-500 to-teal-600'
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: 'Backend Integration',
      description: 'Connecting your frontend to robust backend services and APIs.',
      features: ['REST APIs & MongoDB', 'Authentication systems', 'Database design', 'Serverless functions'],
      gradient: 'from-red-500 to-orange-600'
    }
  ];

  const softwareProjects = [
    {
      title: 'GASSHUB',
      description: 'An Android/iOS app for Ghana Association of Statistics Students to streamline academic activities and career developmental activities.',
      image: '/images/GASSHUB.mp4', // Using video as poster fallback
      video: `${process.env.PUBLIC_URL}/images/GASSHUB.mp4`,
      tags: ['React Native', 'Node.js', 'MongoDB'],
      github: '#',
      demo: '#'
    },
    {
      title: 'Bank Dashboard',
      description: 'Collaborated with team of frontend engineers to implement the figma design of a bank dashboard at InternPulse',
      image: `${process.env.PUBLIC_URL}/images/bank-dashboard.png`,
      tags: ['React', 'Git', 'GitHub', 'Tailwindcss'],
      github: '#',
      demo: '#'
    },
    {
      title: 'Personal Portfolio',
      description: 'Collaborated with team of frontend engineers to implement the figma design of a bank dashboard at InternPulse',
      image: `${process.env.PUBLIC_URL}/images/portfolio.png`,
      tags: ['React', 'Git', 'Tailwindcss'],
      github: '#',
      demo: '#'
    },
    {
      title: 'PAYeasy',
      description: 'An Android/iOS app to streamline school payment of bills, saving time for students and administrators.',
      image: `${process.env.PUBLIC_URL}/images/Payeasy.png`,
      tags: ['React Native', 'Node.js', 'Payment Integration'],
      github: '#',
      demo: '#'
    }
  ];

  const artworks = [
    {
      title: 'Market Day @ Keta',
      description: 'A captured scene of market activities at Keta Market.',
      medium: 'Mosaic art',
      year: '2023',
      image: `${process.env.PUBLIC_URL}/images/market.day.jpg`
    },
    {
      title: 'Fishermen @ Dawn',
      description: 'A scene of men leaving the shore for fishing',
      medium: 'Mosaic art',
      year: '2023',
      image: `${process.env.PUBLIC_URL}/images/fishermendawn.jpg`
    },
    {
      title: 'Keta Christian School',
      description: 'Colorful image of the School surroundings',
      medium: 'Mosaic & Paint Art',
      year: '2022',
      image: `${process.env.PUBLIC_URL}/images/ketaChristianSchool.jpg`
    },
    {
      title: 'Sunflower',
      description: 'Imaginative Drawing',
      medium: 'Mosaic & Paint Art',
      year: '2022',
      image: `${process.env.PUBLIC_URL}/images/sunflower.jpg`
    },
    {
      title: "Mother's Love",
      description: 'Imaginative',
      medium: 'Mosaic & Paint Art',
      year: '2021',
      image: `${process.env.PUBLIC_URL}/images/motherLove.jpg`
    },
    {
      title: 'The Perfect Picture',
      description: 'Charcoal Portrait of My Mother.',
      medium: 'Charcoal & Pencil Art',
      year: '2021',
      image: `${process.env.PUBLIC_URL}/images/perfectpictur.jpg`
    },
    {
      title: "Sophia's Smile",
      description: 'Painted Portrait of Baby Sophia',
      medium: 'Paint Art',
      year: '2021',
      image: `${process.env.PUBLIC_URL}/images/sophiasmile.jpg`
    },
    {
      title: 'Patient Vigil',
      description: 'Imaginative Drawing',
      medium: 'Mosaic Art',
      year: '2020',
      image: `${process.env.PUBLIC_URL}/images/Patientvigil.jpg`
    },
    {
      title: "Gifty's Beauty",
      description: 'A portrait of my dear friend on her birthday',
      medium: 'Portrait Art',
      year: '2021',
      image: `${process.env.PUBLIC_URL}/images/GIFTY'S DRAWING .jpeg`
    }
  ];

  const communityServiceImages = [
    `${process.env.PUBLIC_URL}/images/tut 1.jpg`,
    `${process.env.PUBLIC_URL}/images/tut 2.jpeg`,
    `${process.env.PUBLIC_URL}/images/tut 3.jpeg`,
    `${process.env.PUBLIC_URL}/images/tut 4.jpeg`,
    `${process.env.PUBLIC_URL}/images/tut7.jpeg`,
    `${process.env.PUBLIC_URL}/images/tut8.jpeg`
  ];

  const certificates = [
    {
      title: 'IBM Full Stack Certificate',
      image: `${process.env.PUBLIC_URL}/images/IBM FULL STACK SOFTWARE DEVELOPMENT_page-0001.jpg`
    },
    {
      title: 'Applied Data Science Certificate',
      image: `${process.env.PUBLIC_URL}/images/DATA SCIENCE CERT_page-0001.jpg`
    },
    {
      title: 'Stanford Machine Learning Certificate',
      image: `${process.env.PUBLIC_URL}/images/STANFORD-MACHINE LEARNING CERT_page-0001.jpg`
    },
    {
      title: 'Google Project Management Certificate',
      image: `${process.env.PUBLIC_URL}/images/Google Project Management Cert_page-0001.jpg`
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900'}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${darkMode ? 'bg-gray-900/95 backdrop-blur-lg border-b border-gray-800' : 'bg-white/95 backdrop-blur-lg border-b border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">PRINCE</h1>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['Home', 'About', 'Services', 'Portfolio', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`transition-colors duration-300 hover:text-blue-500 ${
                    activeSection === item.toLowerCase() ? 'text-blue-500 font-semibold' : ''
                  }`}
                >
                  {item}
                </button>
              ))}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>

            <div className="md:hidden flex items-center space-x-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['Home', 'About', 'Services', 'Portfolio', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`block w-full text-left px-3 py-2 rounded-md ${
                    activeSection === item.toLowerCase()
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6 animate-fade-in">
              <div className="relative p-8 rounded-2xl overflow-hidden">
                {/* Animated rotating border */}
                <div className="absolute inset-0 rounded-2xl" style={{background: 'conic-gradient(from 0deg, transparent 0%, transparent 70%, #3b82f6 85%, transparent 100%)', animation: 'border-spin 4s linear infinite'}}></div>
                <div className={`absolute inset-[2px] rounded-2xl ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}></div>
                
                <div className="relative z-10">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4">
                    Hi, I'm <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Prince</span>
                  </h2>
                  <div className="h-12 mb-6">
                    <p className="text-2xl md:text-3xl font-semibold text-blue-500 transition-all duration-500">
                      {roles[currentRole]}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      I build scalable web applications and intelligent machine learning models. With a strong foundation in data structures and algorithms, I write clean, maintainable code that stands the test of time. I thrive in collaborative teams where I can solve complex problems and continuously grow.
                    </p>
                    <p className="text-lg font-semibold text-blue-400">
                      Certified in IBM Full Stack Development, Machine Learning, and Data Science.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <button onClick={() => scrollToSection('contact')} className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2">
                  Contact Me <ExternalLink className="w-4 h-4" />
                </button>
                <a 
                  href={resumePath} 
                  download
                  className={`px-8 py-3 rounded-lg font-semibold border-2 ${darkMode ? 'border-gray-700 hover:border-blue-500' : 'border-gray-300 hover:border-blue-500'} hover:scale-105 transition-all duration-300 flex items-center gap-2`}
                >
                  Download Resume <Download className="w-4 h-4" />
                </a>
              </div>
              <div className="flex gap-4 pt-4">
                <a href="https://www.linkedin.com/in/prince-philip-lartey/" target="_blank" rel="noopener noreferrer" className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:scale-110 transition-transform">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="p-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 hover:scale-110 transition-transform">
                  <Github className="w-5 h-5" />
                </a>
                <a href="mailto:pannohlartey@gmail.com" className="p-3 rounded-full bg-gradient-to-r from-green-500 to-teal-600 hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="relative flex justify-center items-start mt-8 ml-8">
              {/* Circular profile image with animated rotating border */}
              <div className="relative">
                {/* Animated rotating border - same as Hero text box */}
                <div className="absolute -inset-1 rounded-full" style={{background: 'conic-gradient(from 0deg, transparent 0%, transparent 70%, #3b82f6 85%, transparent 100%)', animation: 'spin 4s linear infinite'}}></div>
                
                {/* Profile image */}
                <div className="relative w-80 h-80 rounded-full overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/passport.jpeg`}
                    alt="Prince Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative blur effects */}
                <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-3xl opacity-20 animate-pulse -z-10"></div>
                <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full blur-3xl opacity-20 animate-pulse -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className={`py-20 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-800/50' : 'bg-white/50'}`}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">About Me</h2>
          
          <div className="flex justify-center gap-2 mb-8 flex-wrap">
            {[
              { id: 'about', label: 'About', icon: <Award className="w-4 h-4" /> },
              { id: 'skills', label: 'Skills', icon: <Code className="w-4 h-4" /> },
              { id: 'experience', label: 'Experience', icon: <Briefcase className="w-4 h-4" /> },
              { id: 'education', label: 'Education', icon: <GraduationCap className="w-4 h-4" /> },
              { id: 'certifications', label: 'Certifications', icon: <Award className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1 flex justify-center">
              <div className="rounded-3xl overflow-hidden shadow-2xl max-w-xs w-full">
                {/* About Image */}
                <img
                  src={`${process.env.PUBLIC_URL}/images/about2.jpeg`}
                  alt="Prince About"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              {activeTab === 'about' && (
                <div className="animate-fade-in">
                  <div className="relative p-8 border-2 border-blue-500/30">
                    <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-blue-500"></div>
                    <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-blue-500"></div>
                    <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-blue-500"></div>
                    <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-blue-500"></div>
                    
                    <h3 className="text-3xl font-bold mb-6">About Me</h3>
                    <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      I'm passionate about Data Science and Public Policy to create data-driven solutions for global issues like inequality, mental health, and sustainability. Inspired by my works such as founding a data analytics firm, leading community education initiatives, and contributing to policy research on youth unemployment, I believed combining these fields will train me to analyze complex datasets and translate findings into policies that make a real difference. With skills in statistical modeling, machine learning, and policy analysis, I plan to develop AI tools and evidence-based strategies to tackle pressing issues, such as improving educational access in underserved regions and advocating for mental health reforms. My goal is to work with NGOs, government, or tech-policy groups to drive meaningful change.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'skills' && (
                <div className="space-y-6 animate-fade-in">
                  <h3 className="text-3xl font-bold mb-6">My Tech Stack</h3>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {skills.map((skill, index) => (
                      <div key={index} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} shadow-lg hover:scale-105 transition-transform`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-lg">{skill.name}</span>
                          <span className="text-sm font-bold text-blue-500">{skill.level}%</span>
                        </div>
                        <div className={`h-3 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          <div
                            className={`h-full bg-gradient-to-r ${skill.color} rounded-full transition-all duration-1000`}
                            style={{ width: `${skill.level}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'experience' && (
                <div className="space-y-6 animate-fade-in">
                  <h3 className="text-3xl font-bold mb-6">Professional Journey</h3>
                  <div className="relative border-l-4 border-blue-500 pl-8 space-y-12">
                    <div>
                      <div className="absolute -left-3 w-6 h-6 bg-blue-500 rounded-full"></div>
                      <p className="text-blue-500 font-semibold">Mar 2025 - May 2025</p>
                      <h4 className="text-2xl font-bold mt-2">Full Stack Developer</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>InternPulse · Internship · Remote</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Collaborated with cross-functional teams including product designers, backend engineers, and PMs to develop POS-Padi, a fintech solution for Nigerian POS operators</li>
                        <li>• Implemented responsive UI components from Figma designs and integrated RESTful APIs to fetch and display real-time financial data</li>
                        <li>• Conducted thorough code reviews and debugging sessions with team members to maintain code quality and optimize application performance</li>
                        <li>• Developed a comprehensive freelancer dashboard with Chakra UI and React, featuring expense tracking, project analytics, and client management functionalities</li>
                        <li>• Built a personal portfolio website using React, JavaScript, and Tailwind CSS to showcase technical skills and projects</li>
                      </ul>
                    </div>

                    <div>
                      <div className="absolute -left-3 w-6 h-6 bg-green-500 rounded-full"></div>
                      <p className="text-green-500 font-semibold">Jan 2024 - Jun 2024</p>
                      <h4 className="text-2xl font-bold mt-2">Data Analyst Intern</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ghana Statistical Service · Internship</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Collected and analyzed comprehensive youth unemployment data across multiple regions to identify trends and patterns</li>
                        <li>• Co-authored policy recommendations for Ghana's UN SDG reports, directly influencing national employment strategies</li>
                        <li>• Performed statistical analysis using R and Python to derive actionable insights from large datasets</li>
                        <li>• Created data visualizations and reports for stakeholders, translating complex data into accessible formats for policy makers</li>
                        <li>• Collaborated with government officials and researchers to ensure data accuracy and relevance to national development goals</li>
                      </ul>
                    </div>

                    <div>
                      <div className="absolute -left-3 w-6 h-6 bg-purple-500 rounded-full"></div>
                      <p className="text-purple-500 font-semibold">2023 - Present</p>
                      <h4 className="text-2xl font-bold mt-2">Founder & Data Analyst</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>DataBreach Analysis · Self-Founded</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Founded and established a data analytics firm specializing in business intelligence and predictive analytics</li>
                        <li>• Utilized Python and machine learning algorithms to analyze data for 27 clients across various industries</li>
                        <li>• Provided actionable insights and data-driven recommendations that improved business decision-making and operational efficiency</li>
                        <li>• Developed custom data pipelines and automated reporting systems to streamline data processing workflows</li>
                        <li>• Built and maintained client relationships, delivering tailored analytics solutions that addressed specific business challenges</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'education' && (
                <div className="space-y-6 animate-fade-in">
                  <h3 className="text-3xl font-bold mb-6">Education Background</h3>
                  <div className="space-y-6">
                    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} shadow-lg`}>
                      <p className="text-blue-500 font-semibold">2024</p>
                      <h4 className="text-2xl font-bold mt-2">Full Stack Software Engineering</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>IBM Corporation</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Completed 15 comprehensive courses covering modern application development and cloud technologies</li>
                        <li>• Gained hands-on experience with full-stack technologies including HTML, CSS, JavaScript, React, Node.js, Python, and various database systems (SQL, NoSQL, Django ORM)</li>
                        <li>• Mastered cloud-native practices, DevOps methodologies, CI/CD pipelines, containerization with Docker and Kubernetes, and deployment on OpenShift</li>
                        <li>• Developed expertise in microservices architecture, serverless computing, application security, and Bootstrap framework</li>
                        <li>• Successfully deployed multiple applications on cloud platforms and completed a capstone project delivering a Software as a Service (SaaS) solution using cloud-native methodologies</li>
                      </ul>
                    </div>

                    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} shadow-lg`}>
                      <p className="text-green-500 font-semibold">2024</p>
                      <h4 className="text-2xl font-bold mt-2">Applied Data Science</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>IBM Corporation</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Developed comprehensive skills in Python programming for data science and machine learning applications</li>
                        <li>• Mastered data analysis techniques including data cleaning, manipulation, and statistical analysis using pandas and NumPy</li>
                        <li>• Created sophisticated data visualizations using Matplotlib, Seaborn, and Plotly to communicate insights effectively</li>
                        <li>• Completed a Capstone project applying machine learning algorithms to real-world datasets, demonstrating end-to-end data science workflow from data acquisition to model deployment</li>
                      </ul>
                    </div>

                    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} shadow-lg`}>
                      <p className="text-purple-500 font-semibold">2024</p>
                      <h4 className="text-2xl font-bold mt-2">Machine Learning Specialization</h4>
                      <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stanford University</p>
                      <ul className={`mt-3 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>• Studied modern supervised learning algorithms including linear regression, logistic regression, neural networks, and decision trees for classification and regression problems</li>
                        <li>• Mastered unsupervised learning techniques such as clustering algorithms and anomaly detection for pattern discovery in unlabeled data</li>
                        <li>• Developed expertise in building recommender systems and reinforcement learning for intelligent decision-making applications</li>
                        <li>• Learned industry best practices for building robust machine learning models including feature engineering, model evaluation, and hyperparameter tuning</li>
                        <li>• Gained practical skills to apply machine learning techniques to challenging real-world problems across various domains</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'certifications' && (
                <div className="space-y-6 animate-fade-in">
                  <h3 className="text-3xl font-bold mb-6">My Certifications</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {certificates.map((cert, index) => (
                      <div key={index} className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-700/50' : 'bg-white'} shadow-lg hover:scale-105 transition-all duration-300`}>
                        <div className="relative overflow-hidden">
                          <img
                            src={cert.image}
                            alt={cert.title}
                            className="w-full h-auto object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="text-xl font-bold text-center">{cert.title}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">My Services</h2>
          <p className={`text-center text-lg mb-12 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Comprehensive solutions tailored to bring your digital vision to life with precision and creativity.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`rounded-2xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:scale-105 transition-all duration-300 group overflow-hidden relative`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 text-white`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient}`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className={`py-20 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-800/50' : 'bg-white/50'}`}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">My Portfolio</h2>
          
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            <button 
              onClick={() => setPortfolioTab('software')}
              className={`px-8 py-3 rounded-lg font-semibold shadow-lg flex items-center gap-2 transition-all duration-300 ${
                portfolioTab === 'software' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 scale-105' 
                  : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <Code className="w-5 h-5" />
              Software Projects
            </button>
            <button 
              onClick={() => setPortfolioTab('artworks')}
              className={`px-8 py-3 rounded-lg font-semibold shadow-lg flex items-center gap-2 transition-all duration-300 ${
                portfolioTab === 'artworks' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 scale-105' 
                  : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <Palette className="w-5 h-5" />
              Artworks
            </button>
            <button 
              onClick={() => setPortfolioTab('community')}
              className={`px-8 py-3 rounded-lg font-semibold shadow-lg flex items-center gap-2 transition-all duration-300 ${
                portfolioTab === 'community' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 scale-105' 
                  : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <Award className="w-5 h-5" />
              Community Service
            </button>
          </div>

          {portfolioTab === 'software' && (
            <div className="grid md:grid-cols-2 gap-8">
              {softwareProjects.map((project, index) => (
                <div
                  key={index}
                  className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:scale-105 transition-all duration-300 group`}
                >
                  <div className="relative overflow-hidden h-64">
                    {project.video ? (
                      <video
                        controls
                        className="w-full h-full object-cover"
                        preload="metadata"
                        style={{ backgroundColor: '#000' }}
                        playsInline
                        muted={false}
                      >
                        <source src={project.video} type="video/mp4" />
                        <source src={project.video} type="video/webm" />
                        <p className="text-white p-4">Your browser does not support the video tag or the video format is not supported.</p>
                      </video>
                    ) : (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6 gap-4">
                      <a href={project.github} className="p-3 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
                        <Github className="w-5 h-5" />
                      </a>
                      <a href={project.demo} className="p-3 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                    <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {portfolioTab === 'artworks' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {artworks.map((artwork, index) => (
                <div
                  key={index}
                  className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:scale-105 transition-all duration-300 group`}
                >
                  <div className="relative overflow-hidden h-80">
                    <img
                      src={artwork.image}
                      alt={artwork.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold mb-2">{artwork.title}</h3>
                    <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {artwork.description}
                    </p>
                    <div className="flex justify-between items-center mt-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${darkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                        {artwork.medium}
                      </span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {artwork.year}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {portfolioTab === 'community' && (
            <div className="max-w-5xl mx-auto space-y-8">
              <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl p-8`}>
                <h3 className="text-3xl font-bold mb-4 text-center">Free Math & Science Tutoring Program</h3>
                <p className={`text-center mb-6 text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Empowering students through free educational support in mathematics and science
                </p>
                
                {/* Video Section */}
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <video
                    controls
                    className="w-full h-auto"
                  >
                    <source src={`${process.env.PUBLIC_URL}/images/tut.video.mp4`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>

                {/* Images Grid - 2 columns to show all 6 images clearly */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {communityServiceImages.map((image, index) => (
                    <div
                      key={index}
                      className="rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300"
                    >
                      <img
                        src={image}
                        alt={`Community Service ${index + 1}`}
                        className="w-full h-64 object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Work Together</h2>
          <p className={`text-lg mb-12 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Have a project in mind? Let's collaborate and bring your digital vision to life. I'm always excited to work on new and challenging projects.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <Mail className="w-12 h-12 mx-auto mb-4 text-blue-500" />
              <h3 className="text-xl font-bold mb-2">Email Me</h3>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>pannohlartey@gmail.com</p>
            </div>
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <Phone className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <h3 className="text-xl font-bold mb-2">Call Me</h3>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>+233 257 454 231</p>
            </div>
          </div>

          <div className="space-y-4">
            <a href="mailto:pannohlartey@gmail.com" className="block w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
              <Mail className="w-5 h-5" />
              Send Email Directly
            </a>
            <a href="https://wa.me/233257454231" target="_blank" rel="noopener noreferrer" className="block w-full py-4 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              Message on WhatsApp
            </a>
            <a href="https://www.linkedin.com/in/prince-philip-lartey/" target="_blank" rel="noopener noreferrer" className="block w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
              <Linkedin className="w-5 h-5" />
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-4 sm:px-6 lg:px-8 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto text-center">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            © 2025 Prince Philip Lartey. All rights reserved.
          </p>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Built with React & Tailwind CSS
          </p>
        </div>
      </footer>
    </div>
  );
}
